'use client';

import { useMemo, useState } from 'react';
import { evaluateExpression, type AngleMode } from '@/lib/browser-gencalc-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

interface KeyDef {
  label: string;
  insert?: string;
  action?: 'clear' | 'backspace' | 'equals';
  variant?: 'function' | 'operator' | 'accent';
}

const KEYS: KeyDef[] = [
  { label: 'sin', insert: 'sin(', variant: 'function' },
  { label: 'cos', insert: 'cos(', variant: 'function' },
  { label: 'tan', insert: 'tan(', variant: 'function' },
  { label: 'C', action: 'clear', variant: 'accent' },
  { label: 'DEL', action: 'backspace', variant: 'accent' },

  { label: 'ln', insert: 'ln(', variant: 'function' },
  { label: 'log', insert: 'log(', variant: 'function' },
  { label: '√', insert: 'sqrt(', variant: 'function' },
  { label: '(', insert: '(', variant: 'operator' },
  { label: ')', insert: ')', variant: 'operator' },

  { label: 'π', insert: 'pi' },
  { label: '7', insert: '7' },
  { label: '8', insert: '8' },
  { label: '9', insert: '9' },
  { label: '÷', insert: '/', variant: 'operator' },

  { label: 'e', insert: 'e' },
  { label: '4', insert: '4' },
  { label: '5', insert: '5' },
  { label: '6', insert: '6' },
  { label: '×', insert: '*', variant: 'operator' },

  { label: 'x^y', insert: '^', variant: 'operator' },
  { label: '1', insert: '1' },
  { label: '2', insert: '2' },
  { label: '3', insert: '3' },
  { label: '−', insert: '-', variant: 'operator' },

  { label: 'n!', insert: '!', variant: 'operator' },
  { label: '0', insert: '0' },
  { label: '.', insert: '.' },
  // The parser treats % as a remainder operator, so labelling it "%" next to the
  // arithmetic keys made people read it as "percent of" and get a wrong answer.
  { label: 'mod', insert: '%', variant: 'operator' },
  { label: '+', insert: '+', variant: 'operator' },
];

function keyBackground(variant: KeyDef['variant']): string {
  switch (variant) {
    case 'accent':
      return 'rgba(220, 38, 38, 0.12)';
    case 'operator':
      return 'rgba(59, 130, 246, 0.12)';
    case 'function':
      return 'rgba(148, 163, 184, 0.16)';
    default:
      return 'rgba(148, 163, 184, 0.08)';
  }
}

export default function BrowserScientificCalculator() {
  const [expression, setExpression] = useState('');
  const [angleMode, setAngleMode] = useState<AngleMode>('deg');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const evaluation = useMemo(
    () => evaluateExpression(expression, angleMode),
    [expression, angleMode],
  );

  function handleKey(key: KeyDef) {
    setCopyMessage(null);
    if (key.action === 'clear') {
      setExpression('');
      return;
    }
    if (key.action === 'backspace') {
      setExpression((prev) => prev.slice(0, -1));
      return;
    }
    if (key.action === 'equals') {
      if (evaluation.ok && evaluation.formatted) {
        setExpression(evaluation.formatted);
      }
      return;
    }
    if (key.insert !== undefined) {
      setExpression((prev) => prev + key.insert);
    }
  }

  function handleEquals() {
    if (evaluation.ok && evaluation.formatted) {
      setExpression(evaluation.formatted);
      setCopyMessage(null);
    }
  }

  async function handleCopy() {
    if (!evaluation.ok || !evaluation.formatted) {
      return;
    }
    try {
      await copyText(evaluation.formatted);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  const showError = expression.trim().length > 0 && !evaluation.ok && Boolean(evaluation.error);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Calculator</span>
        <h2 className="tool-live-title">Scientific calculator</h2>
        <p className="tool-live-subtitle">
          A full scientific calculator with trig, logs, powers, roots, and factorials. Expressions
          are parsed safely and evaluated in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Expression</span>
              <input
                onChange={(event) => setExpression(event.target.value)}
                placeholder="e.g. sin(30) + 2^3"
                type="text"
                value={expression}
              />
            </label>
            <label className="tool-live-field">
              <span>Angle mode</span>
              <select
                onChange={(event) => setAngleMode(event.target.value as AngleMode)}
                value={angleMode}
              >
                <option value="deg">Degrees</option>
                <option value="rad">Radians</option>
              </select>
            </label>
          </div>

          <div
            style={{
              display: 'grid',
              gap: '0.4rem',
              gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
              marginTop: '0.75rem',
            }}
          >
            {KEYS.map((key) => (
              <button
                key={key.label}
                onClick={() => handleKey(key)}
                style={{
                  background: keyBackground(key.variant),
                  border: '1px solid rgba(148, 163, 184, 0.35)',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  padding: '0.6rem 0',
                }}
                type="button"
              >
                {key.label}
              </button>
            ))}
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleEquals} type="button">
              Evaluate
            </button>
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy result
            </button>
          </div>
          {showError ? <p className="status-message status-error">{evaluation.error}</p> : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Result</span>
            <input readOnly type="text" value={evaluation.ok ? (evaluation.formatted ?? '') : ''} />
          </label>
          <p className="tool-live-panel-note">
            Angle mode: {angleMode === 'deg' ? 'degrees' : 'radians'}. Expressions are tokenized and
            parsed without eval, then evaluated locally in your browser.
          </p>
        </section>
      </div>
    </div>
  );
}
